<script setup lang="ts">
import { useStore } from 'vuex';
import { computed, ref } from 'vue';
import { useMeta } from 'vue-meta';
import { UserResponseDto } from '@clearbanc/baus-sdk';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';

import DSButton from 'primevue/button';
import SectionHeader from '@/components/SectionHeader.vue';
import TeamUsersTable from '@/pages/team/components/TeamUsersTable.vue';
import TeamUserProfileSidebar from '@/pages/team/components/TeamUserProfileSidebar.vue';
import TeamDeleteUserModal from '@/pages/team/components/TeamDeleteUserModal.vue';
import TeamEditUserModal from '@/pages/team/components/TeamEditUserModal.vue';
import UserInviteModal from '@/pages/users/components/UserInviteModal.vue';
import { RouteNames } from '@/data/routes';

// -----
// Setup
// -----
const store = useStore();
useMeta({
  title: 'Team | Clearco',
});

// ---------
// Props
// ---------
const props = defineProps<{
  openInviteModal?: boolean;
}>();

// ---------
// Variables
// ---------

const allUsers = computed<UserResponseDto[]>(() => store.getters.allUsers);

const selectedUser = ref<UserResponseDto>();
const userToDelete = ref<UserResponseDto>();
const userToEdit = ref<UserResponseDto>();
const isInviteModalVisible = ref(props.openInviteModal);

// -----
// Methods
// -----
const onRowSelect = (data: UserResponseDto) => {
  selectedUser.value = data;
  userToEdit.value = undefined;
};

const openDeleteModal = (user: UserResponseDto) => {
  userToDelete.value = user;
};

const closeDeleteModal = () => {
  userToDelete.value = undefined;
};

const openEditModal = (user: UserResponseDto) => {
  userToEdit.value = user;
};

const closeEditModal = () => {
  userToEdit.value = undefined;
};

const handleEditSuccess = (userId: string) => {
  const updatedUser = allUsers.value.find((user) => user.id === userId);

  // Update selectedUser if the edited user is currently selected in the sidebar
  if (selectedUser.value?.id === userId && updatedUser) {
    selectedUser.value = updatedUser;
  }

  closeEditModal();
};

const openInviteModal = () => {
  analytics.track(SEGMENT_EVENTS.if_user_invite_modal_opened, {
    source: RouteNames.TEAM.INDEX,
  });
  isInviteModalVisible.value = true;
};

const closeInviteModal = () => {
  isInviteModalVisible.value = false;
};

const onDeleteSuccess = () => {
  selectedUser.value = undefined; // Close sidebar
  userToDelete.value = undefined; // Reset delete user state
};
</script>

<template>
  <SectionHeader
    :title="$t('teamPage.title')"
    :subtitle="$t('teamPage.subtitle')"
  />
  <div class="mb-4">
    <DSButton label="Invite New Member" @click="openInviteModal" />
  </div>
  <TeamUsersTable
    :value="allUsers"
    @row-select="onRowSelect"
    @delete-user="openDeleteModal"
  />
  <TeamUserProfileSidebar
    :visible="!!selectedUser"
    :user="selectedUser"
    @hide="selectedUser = undefined"
    @delete-user="openDeleteModal"
    @edit-user="openEditModal"
  />
  <TeamDeleteUserModal
    v-if="userToDelete"
    :visible="!!userToDelete"
    :userToDelete="userToDelete"
    @close-modal="closeDeleteModal"
    @delete-success="onDeleteSuccess"
  />
  <TeamEditUserModal
    :visible="!!userToEdit"
    :userToEdit="userToEdit"
    @close-modal="closeEditModal"
    @save-success="handleEditSuccess"
  />
  <UserInviteModal
    :visible="isInviteModalVisible"
    @close-modal="closeInviteModal"
  />
</template>
